import { lazy, Suspense, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CalendarDays, CreditCard, PenSquare, Radio, UserCircle, UserPlus } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  UserAvatar,
} from '@/components/ui';
import { useAuth } from '@/pages/auth';
import { useMediaQuery } from '@/lib/useMediaQuery';
import { cn } from '@/lib/utils';
import { EVENTS, track } from '@/lib/analytics';

import { userMenu } from './nav-items';
import { userInitials } from './utils';

/** Tailwind classes for a dropdown row whose path matches the active route. */
const ACTIVE_ITEM_CLASS = 'bg-accent text-brand-700 focus:bg-accent focus:text-brand-700';

// Lazy-load the subscription modal — its bundle pulls in the PayPal SDK
// wrapper, which has no business hitting the dashboard's critical path.
const SubscriptionDialog = lazy(() =>
  import('@/pages/subscription').then((m) => ({ default: m.SubscriptionDialog })),
);

/**
 * Avatar-triggered account dropdown. Items come from `userMenu`; the Logout
 * action clears the session and returns to the public home.
 */
export function UserMenu() {
  const user = useAuth((s) => s.user);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [subscriptionOpen, setSubscriptionOpen] = useState(false);

  // Profile Visitors + Invite Friends live in the left dashboard sidebar at
  // lg+. Below lg the sidebar is hidden, so we surface those two destinations
  // inline in the user dropdown so they're still one tap away.
  // Use `1023.98px` (matches Tailwind's `max-lg:` internal value) so we don't
  // double-render at exactly 1024px — the sidebar uses `lg:block` =
  // `min-width: 1024px` which would otherwise show both at the boundary.
  const isBelowLg = useMediaQuery('(max-width: 1023.98px)');

  // Match the sidebar NavLink active behavior — exact match for root paths,
  // prefix match for nested routes (so `/dashboard/blogs/123` keeps the
  // "My Blogs" item highlighted). `/dashboard` is special-cased to
  // require an exact match; otherwise the Dashboard row lights up at
  // the same time as every nested /dashboard/* item.
  const isActive = (path: string) => {
    if (path === '/dashboard') return pathname === '/dashboard';
    return pathname === path || pathname.startsWith(`${path}/`);
  };

  if (!user) return null;

  const fullName = `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim();

  async function handleLogout() {
    await useAuth.getState().logout();
    navigate('/');
  }

  function handleOpenSubscription() {
    track(EVENTS.SUBSCRIPTION_MODAL_SHOWN, {
      trigger: 'manual',
      source: 'user_menu',
    });
    setSubscriptionOpen(true);
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label="Account menu"
          className="rounded-full outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
        >
          <UserAvatar
            src={user.image}
            alt={fullName}
            fallback={userInitials(user)}
            plan={user.plan}
            ring
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="flex flex-col gap-0.5">
            <span className="font-display text-sm">{fullName || 'Account'}</span>
            {user.username ? (
              <span className="text-xs font-normal text-muted-foreground">
                @{user.username}
              </span>
            ) : null}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {/* `userMenu[0]` is Dashboard — rendered above the inline
              Subscription action so the order reads
              Dashboard → Subscription → Edit Profile → Settings → Logout. */}
          {userMenu.slice(0, 1).map((item) => {
            const Icon = item.icon;
            const active = !!item.path && isActive(item.path);
            return (
              <DropdownMenuItem
                key={item.label}
                className={cn(active && ACTIVE_ITEM_CLASS)}
                onSelect={() => {
                  if (item.path) navigate(item.path);
                }}
              >
                <Icon />
                <span>{item.label}</span>
              </DropdownMenuItem>
            );
          })}
          <DropdownMenuItem
            onSelect={() => {
              handleOpenSubscription();
            }}
          >
            <CreditCard />
            <span>Subscription</span>
          </DropdownMenuItem>
          {/* Sidebar-equivalents — only mounted below lg where the left rail
              is hidden. Paths mirror the entries in `dashboardNav`. Active
              styling mirrors the sidebar's NavLink so the dropdown gives
              the same visual cue as the rail it replaces. */}
          {isBelowLg ? (
            <>
              <DropdownMenuItem
                className={cn(isActive('/dashboard/livestream') && ACTIVE_ITEM_CLASS)}
                onSelect={() => {
                  navigate('/dashboard/livestream');
                }}
              >
                <Radio />
                <span>Go Live</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className={cn(isActive('/dashboard/blogs') && ACTIVE_ITEM_CLASS)}
                onSelect={() => {
                  navigate('/dashboard/blogs');
                }}
              >
                <PenSquare />
                <span>My Blogs</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className={cn(isActive('/events/mine') && ACTIVE_ITEM_CLASS)}
                onSelect={() => {
                  navigate('/events/mine');
                }}
              >
                <CalendarDays />
                <span>My Events</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className={cn(isActive('/dashboard/visitors') && ACTIVE_ITEM_CLASS)}
                onSelect={() => {
                  navigate('/dashboard/visitors');
                }}
              >
                <UserCircle />
                <span>Profile Visitors</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className={cn(isActive('/dashboard/invite-friends') && ACTIVE_ITEM_CLASS)}
                onSelect={() => {
                  navigate('/dashboard/invite-friends');
                }}
              >
                <UserPlus />
                <span>Invite Friends</span>
              </DropdownMenuItem>
            </>
          ) : null}
          {userMenu.slice(1).map((item) => {
            const Icon = item.icon;
            const isLogout = item.action === 'logout';
            const active = !isLogout && !!item.path && isActive(item.path);
            return (
              <DropdownMenuItem
                key={item.label}
                className={cn(
                  isLogout && 'text-danger focus:text-danger',
                  active && ACTIVE_ITEM_CLASS,
                )}
                onSelect={() => {
                  if (isLogout) {
                    void handleLogout();
                  } else if (item.path) {
                    navigate(item.path);
                  }
                }}
              >
                <Icon />
                <span>{item.label}</span>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {subscriptionOpen ? (
        <Suspense fallback={null}>
          <SubscriptionDialog
            open={subscriptionOpen}
            onOpenChange={setSubscriptionOpen}
          />
        </Suspense>
      ) : null}
    </>
  );
}
